import { useState, useEffect, useCallback } from "react";
import { loadVapiSdk, getVapi, VAPI_ASSISTANT_ID, VAPI_PUBLIC_KEY, VAPI_TOOLS } from "@/lib/vapi";
import { toast } from "sonner";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import {
  getVapiMealPlans,
  getVapiJobs,
  getVapiJobDetails,
  getVapiDeliveryAreas,
  getVapiDeliveryCutoffs,
  getVapiConsultationInfo,
  getVapiWeeklyMenu,
  getVapiAuthStatus,
  getVapiMyCart,
  addVapiToCart,
  removeVapiFromCart,
  getVapiConsultationAvailability,
  bookVapiAppointment,
  getVapiMyOrders,
  getVapiOrderDetails,
  getVapiMyConsultations,
  getVapiTodaysDishes,
  getVapiMySubscription,
  getVapiMyWallet,
  getVapiDeliveryStatus,
  getVapiMyMeals,
  getVapiMyDeliveryAddress
} from "@/lib/vapi.functions";

// For debugging
if (typeof window !== 'undefined') {
  (window as any).VAPI_DEBUG = {
    start: null,
    isActive: false,
    isConnecting: false
  };
}

export function useVapi() {
  const [isActive, setIsActive] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isLoaded, setIsLoaded] = useState(!!getVapi());
  
  // Sync debug global
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).VAPI_DEBUG.isActive = isActive;
      (window as any).VAPI_DEBUG.isConnecting = isConnecting;
    }
  }, [isActive, isConnecting]);

  const router = useRouterState();
  const navigate = useNavigate();
  
  // Guard: Determine if we are on an admin page
  const isAdminPage = router.location.pathname.startsWith("/admin");
  
  const fetchMealPlans = useServerFn(getVapiMealPlans);
  const fetchJobs = useServerFn(getVapiJobs);
  const fetchJobDetails = useServerFn(getVapiJobDetails);
  const fetchDeliveryAreas = useServerFn(getVapiDeliveryAreas);
  const fetchDeliveryCutoffs = useServerFn(getVapiDeliveryCutoffs);
  const fetchConsultationInfo = useServerFn(getVapiConsultationInfo);
  const fetchWeeklyMenu = useServerFn(getVapiWeeklyMenu);
  const checkAuthStatus = useServerFn(getVapiAuthStatus);
  const fetchMyCart = useServerFn(getVapiMyCart);
  const addToCartFn = useServerFn(addVapiToCart);
  const removeFromCartFn = useServerFn(removeVapiFromCart);
  const fetchAvailability = useServerFn(getVapiConsultationAvailability);
  const bookAppointmentFn = useServerFn(bookVapiAppointment);
  const fetchMyOrders = useServerFn(getVapiMyOrders);
  const fetchOrderDetails = useServerFn(getVapiOrderDetails);
  const fetchMyConsultations = useServerFn(getVapiMyConsultations);
  const fetchTodaysDishes = useServerFn(getVapiTodaysDishes);
  const fetchMySubscription = useServerFn(getVapiMySubscription);
  const fetchMyWallet = useServerFn(getVapiMyWallet);
  const fetchDeliveryStatus = useServerFn(getVapiDeliveryStatus);
  const fetchMyMeals = useServerFn(getVapiMyMeals);
  const fetchMyDeliveryAddress = useServerFn(getVapiMyDeliveryAddress);

  /**
   * Internal function to bind event listeners to the Vapi instance.
   */
  const bindEvents = useCallback((vapi: any) => {
    const onCallStart = () => {
      console.log("[VAPI_EVENT_TRACE] event: call-start, state: started");
      setIsActive(true);
      setIsConnecting(false);
    };

    const onCallEnd = () => {
      console.log("[VAPI_EVENT_TRACE] event: call-end, state: idle");
      setIsActive(false);
      setIsConnecting(false);
      setIsSpeaking(false);
    };

    const onError = (e: any) => {
      console.error("[VAPI_INIT_FAILURE_TRACE] Vapi error event received", {
        name: e?.name,
        message: e?.message,
        code: e?.code,
        details: e?.details || e?.response?.data,
        vapiEvent: "error",
        sessionState: vapi?.status || "unknown"
      });
      
      toast.error("Voice assistant is temporarily unavailable. Please try again.");
      setIsActive(false);
      setIsConnecting(false);
    };

    const onSpeechStart = () => {
      console.log("[VAPI_EVENT_TRACE] event: speech-start");
      setIsSpeaking(true);
    };
    const onSpeechEnd = () => {
      console.log("[VAPI_EVENT_TRACE] event: speech-end");
      setIsSpeaking(false);
    };

    const onMessage = async (message: any) => {
      console.log("[VAPI_EVENT_TRACE] event: message", { type: message?.type });
      if (message.type === "tool-call") {
        const toolCall = message.toolCall || (message.toolCallList && message.toolCallList[0]);
        if (!toolCall) return;
        
        const { name, parameters, toolCallId } = toolCall;
        console.log(`[Vapi] Tool call: ${name}`, parameters);

        try {
          let result: any = null;
          switch (name) {
            case "get_meal_plans":
              result = await fetchMealPlans();
              break;
            case "get_available_jobs":
              result = await fetchJobs();
              break;
            case "get_job_details":
              result = await fetchJobDetails({ data: parameters });
              break;
            case "get_delivery_areas":
              result = await fetchDeliveryAreas();
              break;
            case "get_auth_status":
              result = await checkAuthStatus();
              break;
            case "navigate_to":
              navigate({ to: parameters.path as any });
              result = { success: true };
              break;
            default:
              // Server-side tools are handled via webhook
              console.log(`[Vapi] Tool ${name} is handled server-side.`);
              return;
          }
          if (vapi.send) {
            vapi.send({
              type: "tool-call-result",
              toolCallId,
              result,
            });
          }
        } catch (error) {
          console.error(`[Vapi] Tool execution error (${name}):`, error);
        }
      }
    };

    if (vapi && typeof vapi.on === 'function') {
      vapi.on("call-start", onCallStart);
      vapi.on("call-end", onCallEnd);
      vapi.on("error", onError);
      vapi.on("speech-start", onSpeechStart);
      vapi.on("speech-end", onSpeechEnd);
      vapi.on("message", onMessage);
    }

    return () => {
      if (vapi && typeof vapi.off === 'function') {
        vapi.off("call-start", onCallStart);
        vapi.off("call-end", onCallEnd);
        vapi.off("error", onError);
        vapi.off("speech-start", onSpeechStart);
        vapi.off("speech-end", onSpeechEnd);
        vapi.off("message", onMessage);
      }
    };
  }, [fetchMealPlans, fetchJobs, fetchJobDetails, fetchDeliveryAreas, checkAuthStatus, navigate]);

  const start = useCallback(async () => {
    if (isAdminPage) return;

    const initTraceId = Math.random().toString(36).substring(7);
    console.log(`[VAPI_INIT_TRACE][${initTraceId}] 1. useVapi.start() invoked`);
    setIsConnecting(true);
    
    try {
      console.log(`[VAPI_INIT_TRACE][${initTraceId}] 2. Loading Vapi SDK...`);
      const vapi = await loadVapiSdk();
      if (!vapi) throw new Error("Vapi SDK failed to load");
      
      setIsLoaded(true);
      
      const assistantOverrides = {};
      
      console.log(`[VAPI_MINIMAL_START_TRACE][${initTraceId}] Payload prep:`, {
        assistantId: !!VAPI_ASSISTANT_ID,
        overridePropertyNames: Object.keys(assistantOverrides),
      });

      if (vapi.start) {
        console.log(`[VAPI_INIT_TRACE][${initTraceId}] 3. vapi.start() invoked`);
        try {
          await vapi.start(VAPI_ASSISTANT_ID, assistantOverrides);
          console.log(`[VAPI_INIT_TRACE][${initTraceId}] 4. vapi.start() resolved`);
          bindEvents(vapi);
        } catch (innerError: any) {
          console.error(`[VAPI_INIT_TRACE][${initTraceId}] vapi.start() failed:`, innerError);
          throw innerError;
        }
      } else if (vapi.run) {
        console.log(`[VAPI_INIT_TRACE][${initTraceId}] 3. vapi.run() with object signature`);
        const session = await vapi.run({
          apiKey: VAPI_PUBLIC_KEY,
          assistant: VAPI_ASSISTANT_ID,
          assistantOverrides: assistantOverrides
        });
        console.log(`[VAPI_INIT_TRACE][${initTraceId}] 4. vapi.run() resolved`);
        if (session && typeof session.on === 'function') {
          bindEvents(session);
        } else {
          bindEvents(vapi);
        }
      } else {
        throw new Error("Vapi SDK has no start or run method");
      }
    } catch (error: any) {
      console.error(`[VAPI_INIT_TRACE][${initTraceId}] FAILURE:`, error);
      toast.error("Voice assistant is temporarily unavailable. Please try again.");
      setIsActive(false);
      setIsConnecting(false);
    }
  }, [isAdminPage, bindEvents]);

  const stop = useCallback(() => {
    const vapi = getVapi();
    if (vapi) {
      if (vapi.stop) vapi.stop();
      else if (vapi.terminate) vapi.terminate();
    }
  }, []);

  useEffect(() => {
    if (isAdminPage) return;
    const vapi = getVapi();
    if (vapi?.on) return bindEvents(vapi);
  }, [isLoaded, bindEvents, isAdminPage]);

  return { isActive, isConnecting, isSpeaking, isLoaded, start, stop };
}
